import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  TextField,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../DataLayouts/Header";
import toast from "react-hot-toast";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../../Configurations/Urls";
import axios from "axios";

function RecruitmentSourceTypesForm({ mode }) {
  const { id } = useParams();
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    recruitment_source_type_name: "",
    description: "",
    is_active: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(mode === "edit" || mode === "view");
  const [btnLoading, setBtnLoading] = useState(false);

  useEffect(() => {
    const loadById = async () => {
      try {
        const response = await axios.get(
          `${MAIN_URL}/api/organizations/${org.organization_id}/recruitment-source-type/${id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const a = response?.data?.recruitment_source_type || {};
        setFormData({
          recruitment_source_type_name: a.recruitment_source_type_name || "",
          description: a.description || "",
          is_active: a.is_active == 1 || a.is_active === true,
        });
      } catch (err) {
        console.error(err);
        toast.error("Failed to load source type");
      } finally {
        setLoading(false);
      }
    };

    if ((mode === "edit" || mode === "view") && id && org?.organization_id) {
      setLoading(true);
      loadById();
    }
  }, [mode, id, org?.organization_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.recruitment_source_type_name?.trim()) {
      errors.recruitment_source_type_name = "Type name is required.";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);

    const payload = {
      recruitment_source_type_name: formData.recruitment_source_type_name,
      description: formData.description || null,
      is_active: !!formData.is_active,
    };

    try {
      if (mode === "edit") {
        await axios.put(
          `${MAIN_URL}/api/organizations/${org?.organization_id}/recruitment-source-type/${id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      } else {
        await axios.post(
          `${MAIN_URL}/api/organizations/${org?.organization_id}/recruitment-source-type`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      }

      toast.success(
        mode === "edit" ? "Source Type Updated!" : "Source Type Created!"
      );
      navigate(-1);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 422) {
        const validationErrors = err.response.data.errors || {};
        setFormErrors(validationErrors);
        toast.error(
          Object.values(validationErrors)
            .map((arr) => (Array.isArray(arr) ? arr[0] : arr))
            .join(" ") || "Validation failed."
        );
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <Header
      mode={mode}
      updateMessage={"Recruitment Source Type"}
      addMessage={"Recruitment Source Type"}
      homeLink={"/recruitment/source-types"}
      homeText={"Recruitment Source Types"}
      onSubmit={handleSubmit}
      loading={loading}
      btnLoading={btnLoading}
    >
      {loading ? (
        <Grid container spacing={2}>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Paper elevation={4} sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Type Name"
                    name="recruitment_source_type_name"
                    disabled={mode === "view"}
                    value={formData.recruitment_source_type_name}
                    onChange={handleChange}
                    error={!!formErrors.recruitment_source_type_name}
                    helperText={formErrors.recruitment_source_type_name}
                    required
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    disabled={mode === "view"}
                    multiline
                    minRows={3}
                    maxRows={5}
                    inputProps={{ maxLength: 255 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.is_active}
                        disabled={mode === "view"}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            is_active: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Active"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || btnLoading || mode === "view"}
                    sx={{ minWidth: 120, textTransform: "capitalize" }}
                  >
                    {btnLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Submit"
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Header>
  );
}

export default RecruitmentSourceTypesForm;
